// src/components/invoices/InvoiceDetail.jsx
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  FileText,
  Mail,
  Phone,
  User,
  Calendar,
  DollarSign,
  Edit,
  Send,
  Trash2,
  XCircle,
  Download,
  Receipt,
  Clock,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

// Import shadcn components
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Separator } from '../ui/separator';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Label } from '../ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';

// Import custom components
import PageContainer from '../ui/layout/PageContainer/PageContainer';

// Import hooks
import { useInvoices } from '../../hooks/useInvoices';
import { useCustomers } from '../../hooks/useCustomers';
import { cn } from '../../utils/cn';

const InvoiceDetail = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();

  // Hooks for data access
  const {
    selectedInvoice,
    selectedInvoiceLoading,
    loadInvoiceDetails,
    sendInvoice,
    cancelInvoice,
    deleteInvoice,
    clearSelectedInvoice,
    sendingInvoice,
    voidingInvoice,
    deletingInvoice,
    isConnected
  } = useInvoices();

  const { getCustomerById } = useCustomers();

  // Local state
  const [activeSection, setActiveSection] = useState('overview');

  // Section refs for scroll-to functionality
  const sectionRefs = {
    overview: useRef(null),
    lineItems: useRef(null),
    customer: useRef(null),
    activity: useRef(null),
    additionalInfo: useRef(null)
  };

  // Store calculated section positions
  const sectionPositions = useRef({});

  // Get invoice data
  const invoice = useMemo(() => {
    if (!selectedInvoice) return null;
    return selectedInvoice;
  }, [selectedInvoice]);

  // Get customer data
  const customer = useMemo(() => {
    if (invoice?.customer_id) {
      return getCustomerById(invoice.customer_id);
    }
    return null;
  }, [invoice?.customer_id, getCustomerById]);

  // Extract data for use in other hooks
  const customerName = invoice?.customer_name || customer?.name || 'Unknown Customer';
  const customerEmail = invoice?.customer_email || customer?.email || '';
  const paidAmount = parseFloat(invoice?.paid_amount || 0);

  // Generate timeline events for activity section - MUST be before early returns
  const timelineEvents = useMemo(() => {
    if (!invoice) return [];

    const events = [];

    // Created event
    events.push({
      id: 'created',
      title: 'Invoice Created',
      description: `Invoice #${invoice.invoice_number} created`,
      timestamp: invoice.created_at,
      icon: FileText,
      color: 'text-slate-400',
      bgColor: 'bg-slate-400/10',
      borderColor: 'border-slate-400/30'
    });

    // Sent event
    if (invoice.sent_date) {
      events.push({
        id: 'sent',
        title: 'Invoice Sent',
        description: `Sent to ${customerEmail}`,
        timestamp: invoice.sent_date,
        icon: Send,
        color: 'text-blue-400',
        bgColor: 'bg-blue-400/10',
        borderColor: 'border-blue-400/30'
      });
    }

    // Paid event
    if (invoice.paid_date) {
      events.push({
        id: 'paid',
        title: 'Payment Received',
        description: `Amount: ${formatCurrency(paidAmount)}`,
        timestamp: invoice.paid_date,
        icon: CheckCircle,
        color: 'text-green-400',
        bgColor: 'bg-green-400/10',
        borderColor: 'border-green-400/30'
      });
    }

    // Sort by timestamp (newest first)
    events.sort((a, b) => {
      const dateA = new Date(a.timestamp);
      const dateB = new Date(b.timestamp);
      return dateB - dateA;
    });

    return events;
  }, [invoice, customerEmail, paidAmount]);

  // Load invoice details on mount
  useEffect(() => {
    if (isConnected && invoiceId && !invoice) {
      console.log('Loading invoice details for:', invoiceId);
      loadInvoiceDetails(invoiceId);
    }
  }, [isConnected, invoiceId, invoice, loadInvoiceDetails]);

  // Clear selected invoice when component unmounts
  useEffect(() => {
    return () => {
      clearSelectedInvoice();
    };
  }, [clearSelectedInvoice]);

  // Canonical: deletingInvoice flag watcher — navigate on server confirmation
  const prevDeletingInvoice = useRef(false);
  useEffect(() => {
    if (prevDeletingInvoice.current && !deletingInvoice) {
      navigate('/invoices');
    }
    prevDeletingInvoice.current = deletingInvoice;
  }, [deletingInvoice, navigate]);

  // Calculate section positions once when invoice loads
  useEffect(() => {
    if (!invoice) return;

    const calculatePositions = () => {
      const contentDiv = sectionRefs.overview?.current?.parentElement;
      if (!contentDiv) return;

      const containerTop = contentDiv.scrollTop;
      const positions = {};

      Object.entries(sectionRefs).forEach(([id, ref]) => {
        if (ref.current) {
          const rect = ref.current.getBoundingClientRect();
          const parentRect = contentDiv.getBoundingClientRect();
          positions[id] = rect.top - parentRect.top + containerTop;
        }
      });

      sectionPositions.current = positions;
    };

    setTimeout(calculatePositions, 100);
  }, [invoice]);

  // Scroll spy
  useEffect(() => {
    if (!invoice) return;

    const contentDiv = sectionRefs.overview?.current?.parentElement;
    if (!contentDiv) return;

    const handleScroll = () => {
      const scrollTop = contentDiv.scrollTop;
      const scrollHeight = contentDiv.scrollHeight;
      const clientHeight = contentDiv.clientHeight;
      const positions = sectionPositions.current;

      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 20;

      if (isAtBottom) {
        setActiveSection(prev => prev !== 'additionalInfo' ? 'additionalInfo' : prev);
        return;
      }

      const sectionOrder = ['additionalInfo', 'activity', 'customer', 'lineItems', 'overview'];

      for (const sectionId of sectionOrder) {
        const sectionTop = positions[sectionId];
        if (sectionTop !== undefined && scrollTop >= sectionTop - 100) {
          setActiveSection(prev => prev !== sectionId ? sectionId : prev);
          break;
        }
      }
    };

    contentDiv.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      contentDiv.removeEventListener('scroll', handleScroll);
    };
  }, [invoice]);

  // Scroll to section
  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    sectionRefs[sectionId]?.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };

  // Helper functions
  const getInitials = (name) => {
    if (!name) return 'UC';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(parseFloat(amount || 0));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'sent':
        return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      case 'draft':
        return 'bg-slate-500/20 text-slate-300 border-slate-500/30';
      case 'partially_paid':
        return 'bg-amber-500/20 text-amber-300 border-amber-500/30';
      case 'overdue':
        return 'bg-red-500/20 text-red-300 border-red-500/30';
      case 'cancelled':
        return 'bg-red-500/20 text-red-300 border-red-500/30';
      default:
        return '';
    }
  };

  // Event handlers
  const handleBack = () => {
    clearSelectedInvoice();
    navigate('/invoices');
  };

  const handleEditInvoice = () => {
    if (invoice?.status === 'draft') {
      navigate(`/invoices/edit/${invoiceId}`);
    }
  };

  const handleSendInvoice = () => {
    if (!invoice || sendingInvoice) return;
    sendInvoice(invoiceId, 'email', '');
  };

  const handleVoidInvoice = () => {
    if (!invoice || voidingInvoice) return;

    if (window.confirm(`Are you sure you want to void invoice ${invoice.invoice_number}? This action cannot be undone.`)) {
      cancelInvoice(invoiceId);
    }
  };

  const handleDeleteInvoice = () => {
    if (!invoice || deletingInvoice) return;

    if (window.confirm(`Are you sure you want to delete invoice ${invoice.invoice_number}? This action cannot be undone.`)) {
      deleteInvoice(invoiceId);
      // Navigation handled by deletingInvoice flag watcher
    }
  };

  const handleRecordPayment = () => {
    console.log('Record payment for invoice:', invoiceId);
    // TODO: Implement payment recording UI
  };

  const handleDownloadPDF = () => {
    console.log('Download PDF for invoice:', invoiceId);
    // TODO: Implement PDF download
  };

  const handleViewCustomer = () => {
    if (invoice?.customer_id) {
      navigate(`/customers/${invoice.customer_id}`);
    }
  };

  // Connection status warning
  if (!isConnected) {
    return (
      <PageContainer>
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>
            Not connected to server. Please check your connection and try again.
          </AlertDescription>
        </Alert>
        <div className="text-center py-16">
          <h2 className="text-2xl font-bold mb-2">Connection Required</h2>
          <p className="text-muted-foreground mb-6">
            Please check your internet connection and try again.
          </p>
          <Button onClick={handleBack}>
            Back to Invoices
          </Button>
        </div>
      </PageContainer>
    );
  }

  // Loading state
  if (selectedInvoiceLoading && !invoice) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading invoice details...</p>
          </div>
        </div>
      </PageContainer>
    );
  }

  // Invoice not found
  if (!selectedInvoiceLoading && !invoice) {
    return (
      <PageContainer>
        <div className="text-center py-16">
          <h2 className="text-2xl font-bold mb-2">Invoice Not Found</h2>
          <p className="text-muted-foreground mb-6">
            The invoice you're looking for doesn't exist or has been removed.
          </p>
          <Button onClick={handleBack}>
            Back to Invoices
          </Button>
        </div>
      </PageContainer>
    );
  }

  const status = invoice?.status || 'draft';
  const customerPhone = customer?.phone || '';

  const lineItems = invoice?.line_items || [];
  const subtotal = parseFloat(invoice?.subtotal || 0);
  const taxAmount = parseFloat(invoice?.tax_amount || 0);
  const totalAmount = parseFloat(invoice?.total_amount || 0);
  const balanceDue = invoice?.balance_due || (totalAmount - paidAmount);

  // Navigation sections
  const navSections = [
    { id: 'overview', label: 'Overview' },
    { id: 'lineItems', label: 'Line Items' },
    { id: 'customer', label: 'Customer' },
    { id: 'activity', label: 'Activity' },
    ...(invoice?.payment_terms || invoice?.notes ? [{ id: 'additionalInfo', label: 'Additional Info' }] : [])
  ];

  // Status-based action buttons
  const getActionButtons = () => {
    const isDraft = status === 'draft';
    const isSent = status === 'sent';
    const isPaid = status === 'paid';
    const isPartiallyPaid = status === 'partially_paid';
    const isOverdue = status === 'overdue';
    const isCancelled = status === 'cancelled';

    return (
      <>
        <Button variant="outline" size="sm" onClick={handleBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        {isDraft && (
          <>
            <Button variant="outline" size="sm" onClick={handleEditInvoice}>
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
            <Button size="sm" onClick={handleSendInvoice} disabled={sendingInvoice}>
              {sendingInvoice ? (
                <>
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Invoice
                </>
              )}
            </Button>
            <Button variant="destructive" size="sm" onClick={handleDeleteInvoice} disabled={deletingInvoice}>
              {deletingInvoice ? (
                <>
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </>
              )}
            </Button>
          </>
        )}

        {(isSent || isPartiallyPaid || isOverdue) && (
          <>
            <Button variant="outline" size="sm" onClick={handleSendInvoice} disabled={sendingInvoice}>
              {sendingInvoice ? (
                <>
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                  Resending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Resend
                </>
              )}
            </Button>
            <Button variant="outline" size="sm" onClick={handleRecordPayment}>
              <Receipt className="w-4 h-4 mr-2" />
              Record Payment
            </Button>
            <Button variant="outline" size="sm" onClick={handleDownloadPDF}>
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
            <Button variant="destructive" size="sm" onClick={handleVoidInvoice} disabled={voidingInvoice}>
              {voidingInvoice ? (
                <>
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                  Voiding...
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4 mr-2" />
                  Void
                </>
              )}
            </Button>
          </>
        )}

        {isPaid && (
          <>
            <Button variant="outline" size="sm" onClick={handleDownloadPDF}>
              <Download className="w-4 h-4 mr-2" />
              Download Receipt
            </Button>
            <Button variant="outline" size="sm" onClick={handleDownloadPDF}>
              <Receipt className="w-4 h-4 mr-2" />
              View Receipt
            </Button>
          </>
        )}

        {isCancelled && (
          <Button variant="outline" size="sm" onClick={handleDownloadPDF}>
            <Download className="w-4 h-4 mr-2" />
            Download PDF
          </Button>
        )}
      </>
    );
  };

  return (
    <PageContainer heightMode="viewport" maxWidth="full" padding="none">
      <div className="h-full flex flex-col bg-gradient-to-tl from-[#0a0a0a] from-0% via-[#0b0b0f] via-20% via-[#0c0c18] via-40% via-[#0b0b12] via-60% via-[#0b0b0e] via-80% to-[#0a0a0a] to-100%">
        {/* Main Content */}
        <div className="flex-1 overflow-hidden">
          <div className="h-full pt-12 pl-36 pr-36 flex flex-col">
            {/* Action Buttons */}
            <div className="flex justify-end mb-2">
              <div className="flex gap-2">
                {getActionButtons()}
              </div>
            </div>

            {/* Header */}
            <div className="mb-6">
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <h1 className="text-4xl font-bold text-foreground">
                    Invoice #{invoice.invoice_number}
                  </h1>
                  <Badge className={getStatusColor(status)}>
                    {status === 'paid' && <CheckCircle className="w-3 h-3 mr-1" />}
                    {status === 'sent' && <Mail className="w-3 h-3 mr-1" />}
                    {status === 'draft' && <FileText className="w-3 h-3 mr-1" />}
                    {status === 'overdue' && <AlertCircle className="w-3 h-3 mr-1" />}
                    {status === 'cancelled' && <XCircle className="w-3 h-3 mr-1" />}
                    {status === 'partially_paid' && <DollarSign className="w-3 h-3 mr-1" />}
                    {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                  </Badge>
                </div>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4" />
                    <span>Created {formatDate(invoice.invoice_date)}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4" />
                    <span>Due {invoice.due_date ? formatDate(invoice.due_date) : 'on receipt'}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <DollarSign className="w-4 h-4" />
                    <span className="font-semibold text-foreground">{formatCurrency(totalAmount)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Side Nav and Content */}
            <div className="flex-1 flex min-h-0">
              {/* Side Navigation */}
              <div className="w-40 flex-shrink-0">
                <div className="space-y-1 sticky top-0">
                  {navSections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={cn(
                        "block w-full text-left text-sm py-1 transition-colors",
                        activeSection === section.id
                          ? "text-foreground font-medium"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      {section.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Content - scrollable */}
              <div className="flex-1 min-w-0 overflow-y-auto pl-2 space-y-10 pb-12">
                {/* Overview Section */}
                <div ref={sectionRefs.overview} data-section="overview" className="scroll-mt-6 space-y-5">
                  <div>
                    <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                      <FileText className="w-5 h-5" />
                      Invoice Summary
                    </h3>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      Key details and financial summary for this invoice
                    </p>
                  </div>

                  <div className="grid grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Invoice Number</Label>
                      <p className="text-base">#{invoice.invoice_number}</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Status</Label>
                      <Badge className={getStatusColor(status)}>
                        {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Invoice Date</Label>
                      <p className="text-base">{formatDate(invoice.invoice_date)}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Due Date</Label>
                      <p className="text-base">{invoice.due_date ? formatDate(invoice.due_date) : 'Due on receipt'}</p>
                    </div>
                    {invoice.sent_date && (
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Sent Date</Label>
                        <p className="text-base">{formatDate(invoice.sent_date)}</p>
                      </div>
                    )}
                    {invoice.paid_date && (
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Paid Date</Label>
                        <p className="text-base">{formatDate(invoice.paid_date)}</p>
                      </div>
                    )}
                  </div>

                  <Separator />

                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Total Amount</Label>
                      <p className="text-2xl font-bold">{formatCurrency(totalAmount)}</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Balance Due</Label>
                      <p className="text-2xl font-bold text-emerald-400">{formatCurrency(balanceDue)}</p>
                    </div>
                  </div>

                  {paidAmount > 0 && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Paid Amount</Label>
                      <p className="text-base text-muted-foreground">{formatCurrency(paidAmount)}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground pt-2">
                    <div>
                      <span className="font-medium">Created:</span> {formatDateTime(invoice?.created_at)}
                    </div>
                    <div>
                      <span className="font-medium">Last Updated:</span> {formatDateTime(invoice?.updated_at)}
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Line Items Section */}
                <div ref={sectionRefs.lineItems} data-section="lineItems" className="scroll-mt-6 space-y-5">
                  <div>
                    <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                      <Receipt className="w-5 h-5" />
                      Line Items
                    </h3>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      Services and products included in this invoice
                    </p>
                  </div>

                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Quantity</TableHead>
                        <TableHead className="text-right">Unit Price</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {lineItems.map((item, index) => (
                        <TableRow key={item.id || index}>
                          <TableCell className="font-medium">{item.description}</TableCell>
                          <TableCell className="text-right">{item.quantity}</TableCell>
                          <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
                          <TableCell className="text-right font-medium">{formatCurrency(item.total_amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  <Separator />

                  {/* Totals */}
                  <div className="space-y-3 max-w-md ml-auto">
                    <div className="flex justify-between text-sm">
                      <Label className="text-muted-foreground">Subtotal</Label>
                      <span className="font-medium">{formatCurrency(subtotal)}</span>
                    </div>
                    {taxAmount > 0 && (
                      <div className="flex justify-between text-sm">
                        <Label className="text-muted-foreground">
                          Tax {invoice.tax_rate && `(${(parseFloat(invoice.tax_rate) * 100).toFixed(2)}%)`}
                        </Label>
                        <span className="font-medium">{formatCurrency(taxAmount)}</span>
                      </div>
                    )}
                    <Separator />
                    <div className="flex justify-between text-lg font-bold">
                      <Label>Total</Label>
                      <span>{formatCurrency(totalAmount)}</span>
                    </div>
                    {paidAmount > 0 && (
                      <>
                        <div className="flex justify-between text-sm text-muted-foreground">
                          <Label>Paid</Label>
                          <span>-{formatCurrency(paidAmount)}</span>
                        </div>
                        <div className="flex justify-between text-lg font-bold text-emerald-400">
                          <Label>Balance Due</Label>
                          <span>{formatCurrency(balanceDue)}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <Separator />

                {/* Customer Section */}
                <div ref={sectionRefs.customer} data-section="customer" className="scroll-mt-6 space-y-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                        <User className="w-5 h-5" />
                        Customer Information
                      </h3>
                      <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                        Contact details for the customer associated with this invoice
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleViewCustomer}
                    >
                      View Full Profile
                    </Button>
                  </div>

                  <div className="flex items-start gap-6">
                    <Avatar className="h-20 w-20 shrink-0">
                      <AvatarFallback className="text-2xl font-semibold">
                        {getInitials(customerName)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 space-y-4">
                      <div>
                        <h4 className="text-xl font-semibold mb-1">{customerName}</h4>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        {customerEmail && (
                          <div className="space-y-1">
                            <Label className="text-sm font-medium flex items-center gap-2">
                              <Mail className="w-4 h-4" />
                              Email
                            </Label>
                            <p className="text-base text-muted-foreground">{customerEmail}</p>
                          </div>
                        )}
                        {customerPhone && (
                          <div className="space-y-1">
                            <Label className="text-sm font-medium flex items-center gap-2">
                              <Phone className="w-4 h-4" />
                              Phone
                            </Label>
                            <p className="text-base text-muted-foreground">{customerPhone}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Activity Section */}
                <div ref={sectionRefs.activity} data-section="activity" className="scroll-mt-6 space-y-5">
                  <div>
                    <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                      <Clock className="w-5 h-5" />
                      Activity Timeline
                      {timelineEvents.length > 0 && (
                        <span className="ml-2 px-2 py-0.5 bg-primary/20 text-primary text-xs rounded-full font-normal">
                          {timelineEvents.length} {timelineEvents.length === 1 ? 'Event' : 'Events'}
                        </span>
                      )}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      History of events for this invoice
                    </p>
                  </div>

                  <div className="relative pl-8">
                    {/* Timeline Line */}
                    <div className="absolute left-[15px] top-0 bottom-0 w-px bg-border" />

                    {/* Timeline Items */}
                    <div className="space-y-4">
                      {timelineEvents.map((event, index) => {
                        const Icon = event.icon;
                        const isLast = index === timelineEvents.length - 1;

                        return (
                          <div
                            key={event.id}
                            className="relative group"
                          >
                            {/* Timeline Dot */}
                            <div
                              className={cn(
                                "absolute -left-8 top-0 w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all",
                                event.borderColor,
                                event.bgColor,
                                isLast && "ring-4 ring-border/50"
                              )}
                            >
                              <Icon className={cn("w-4 h-4", event.color)} />
                            </div>

                            {/* Event Content */}
                            <div className="p-4 rounded-lg border border-border bg-zinc-900/50 hover:bg-zinc-900/80 transition-colors">
                              <div className="flex items-start justify-between mb-2">
                                <h4 className="text-sm font-semibold">{event.title}</h4>
                                <span className="text-xs text-muted-foreground whitespace-nowrap ml-4">
                                  {formatDateTime(event.timestamp)}
                                </span>
                              </div>
                              <p className="text-sm text-muted-foreground">{event.description}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Additional Information Section */}
                {(invoice?.payment_terms || invoice?.notes) && (
                  <>
                    <Separator />
                    <div ref={sectionRefs.additionalInfo} data-section="additionalInfo" className="scroll-mt-6 space-y-5">
                      <div>
                        <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                          <FileText className="w-5 h-5" />
                          Additional Information
                        </h3>
                        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                          Payment terms and notes for this invoice
                        </p>
                      </div>

                      {invoice.payment_terms && (
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Payment Terms</Label>
                          <p className="text-base text-muted-foreground">{invoice.payment_terms}</p>
                        </div>
                      )}

                      {invoice.notes && (
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Notes</Label>
                          <p className="text-base text-muted-foreground whitespace-pre-wrap">{invoice.notes}</p>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default InvoiceDetail;
